import React, { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';

interface PointApplication {
  id: number;
  user_id: string;
  points_requested: number;
  amount_usd: number;
  email: string;
  phone: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  admin_notes?: string;
}

interface UserPoints {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  points: number;
  created_at: string;
  avatar_url?: string;
}

// 🆕 Profil Doğrulama Interface
interface ProfileVerification {
  id: number;
  user_id: string;
  name: string;
  surname: string;
  phone: string;
  email: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at?: string;
  admin_notes?: string;
  user?: {
    name: string;
    profile_pic_url?: string;
  };
}

const PointApplicationsPage: React.FC = () => {
  const [applications, setApplications] = useState<PointApplication[]>([]);
  const [selectedApplication, setSelectedApplication] = useState<PointApplication | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'user-points' | 'verifications'>('all');
  const [userPoints, setUserPoints] = useState<UserPoints[]>([]);
  const [pointsLoading, setPointsLoading] = useState(false);
  
  // 🆕 Profil doğrulama state'leri
  const [verifications, setVerifications] = useState<ProfileVerification[]>([]);
  const [verificationsLoading, setVerificationsLoading] = useState(false);

  useEffect(() => {
    fetchApplications();
    fetchVerifications(); // 🆕 Doğrulamaları da yükle
  }, []);

  useEffect(() => {
    if (filter === 'user-points') {
      fetchUserPoints();
    } else if (filter === 'verifications') {
      fetchVerifications();
    }
  }, [filter]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('cashout_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setApplications(data || []);
    } catch (error) {
      console.error('Başvurular yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPoints = async () => {
    try {
      setPointsLoading(true);
      console.log('🔍 Kullanıcı puanları sorgulanıyor...');
      
      // Önce user_points tablosundan verileri çek
      const { data: pointsData, error: pointsError } = await supabase
        .from('user_points')
        .select('user_id, available_points, total_points, created_at')
        .order('available_points', { ascending: false })
        .limit(100);

      if (pointsError) throw pointsError;

      console.log('📊 User Points Response:', { pointsData, pointsError });
      console.log('👥 Puan alan kullanıcı sayısı:', pointsData?.length || 0);

      if (!pointsData || pointsData.length === 0) {
        setUserPoints([]);
        return;
      }

      // User ID'leri al
      const userIds = pointsData.map(p => p.user_id);

      // Users tablosundan kullanıcı bilgilerini çek
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, email, name, profile_pic_url')
        .in('id', userIds);

      if (usersError) throw usersError;

      console.log('👤 Users Response:', { usersData, usersError });

      // İki tablodan gelen verileri birleştir
      const transformedData = pointsData.map(pointItem => {
        const userItem = usersData?.find(u => u.id === pointItem.user_id);
        return {
          id: pointItem.user_id,
          email: userItem?.email || 'Bilinmiyor',
          first_name: userItem?.name || 'Bilinmiyor',
          last_name: '',
          points: pointItem.available_points,
          created_at: pointItem.created_at,
          avatar_url: userItem?.profile_pic_url
        };
      });

      console.log('💰 İlk 5 kullanıcının puan durumu:', 
        transformedData.slice(0, 5).map(u => ({
          name: `${u.first_name} ${u.last_name}`,
          email: u.email,
          points: u.points
        }))
      );
      
      setUserPoints(transformedData);
    } catch (error) {
      console.error('Kullanıcı puanları yüklenirken hata:', error);
    } finally {
      setPointsLoading(false);
    }
  };

  // 🆕 Profil doğrulamalarını getir
  const fetchVerifications = async () => {
    try {
      setVerificationsLoading(true);
      console.log('🔍 Profil doğrulamaları sorgulanıyor...');
      
      const { data, error } = await supabase
        .from('profile_verifications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Doğrulama sorgu hatası:', error);
        throw error;
      }

      console.log('📋 Doğrulama başvuruları:', data?.length || 0);

      // Kullanıcı bilgilerini de al
      if (data && data.length > 0) {
        const userIds = data.map(v => v.user_id);
        const { data: usersData } = await supabase
          .from('users')
          .select('id, name, profile_pic_url')
          .in('id', userIds);

        const verificationsWithUsers = data.map(v => ({
          ...v,
          user: usersData?.find(u => u.id === v.user_id)
        }));

        setVerifications(verificationsWithUsers);
      } else {
        setVerifications([]);
      }
    } catch (error) {
      console.error('Doğrulamalar yüklenirken hata:', error);
    } finally {
      setVerificationsLoading(false);
    }
  };

  // 🆕 Doğrulama durumunu güncelle
  const updateVerificationStatus = async (
    verificationId: number,
    newStatus: 'approved' | 'rejected',
    notes: string
  ) => {
    setActionLoading(true);
    try {
      // Önce doğrulama kaydını al (user_id için)
      const verification = verifications.find(v => v.id === verificationId);
      
      // Doğrulama durumunu güncelle
      const { error } = await supabase
        .from('profile_verifications')
        .update({
          status: newStatus,
          admin_notes: notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', verificationId);

      if (error) throw error;

      // 🔵 Onaylandıysa kullanıcıya mavi tik + 1 aylık premium abonelik ekle
      if (newStatus === 'approved' && verification?.user_id) {
        // Mavi tik ekle
        const { error: userError } = await supabase
          .from('users')
          .update({ is_verified: true })
          .eq('id', verification.user_id);
        
        if (userError) {
          console.error('Kullanıcı verified durumu güncellenirken hata:', userError);
        } else {
          console.log('✅ Kullanıcıya mavi tik eklendi:', verification.user_id);
        }

        // 🎁 1 Aylık Premium Abonelik Ver
        const now = new Date();
        const oneMonthLater = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 gün sonra
        
        // Önce mevcut abonelik var mı kontrol et
        const { data: existingSub } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', verification.user_id)
          .single();

        if (existingSub) {
          // Mevcut aboneliği güncelle - bitiş tarihini uzat veya premium yap
          const currentEndDate = existingSub.end_date ? new Date(existingSub.end_date) : now;
          const newEndDate = currentEndDate > now 
            ? new Date(currentEndDate.getTime() + 30 * 24 * 60 * 60 * 1000) // Mevcut süreye ekle
            : oneMonthLater; // Yeni süre başlat
          
          await supabase
            .from('subscriptions')
            .update({
              subscription_type: 'premium',
              is_active: true,
              start_date: now.toISOString(),
              end_date: newEndDate.toISOString(),
              updated_at: now.toISOString(),
              source: 'profile_verification_reward'
            })
            .eq('user_id', verification.user_id);
          
          console.log('✅ Mevcut abonelik güncellendi - 1 ay premium eklendi');
        } else {
          // Yeni abonelik kaydı oluştur
          await supabase
            .from('subscriptions')
            .insert({
              user_id: verification.user_id,
              subscription_type: 'premium',
              is_active: true,
              start_date: now.toISOString(),
              end_date: oneMonthLater.toISOString(),
              created_at: now.toISOString(),
              source: 'profile_verification_reward'
            });
          
          console.log('✅ Yeni premium abonelik oluşturuldu - 1 ay');
        }
        
        console.log('🎁 Kullanıcıya 1 aylık premium abonelik verildi:', verification.user_id);
      }
      
      // Reddedildiyse mavi tiki kaldır
      if (newStatus === 'rejected' && verification?.user_id) {
        await supabase
          .from('users')
          .update({ is_verified: false })
          .eq('id', verification.user_id);
      }

      await fetchVerifications();
      setAdminNotes('');
      
      alert(`Doğrulama ${newStatus === 'approved' ? 'onaylandı ve 1 aylık premium abonelik verildi!' : 'reddedildi!'}`);
    } catch (error) {
      console.error('Doğrulama güncellenirken hata:', error);
      alert('Hata oluştu!');
    } finally {
      setActionLoading(false);
    }
  };

  const updateApplicationStatus = async (
    applicationId: number,
    newStatus: 'approved' | 'rejected',
    notes: string
  ) => {
    setActionLoading(true);
    try {
      const { error } = await supabase
        .from('cashout_applications')
        .update({
          status: newStatus,
          admin_notes: notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', applicationId);

      if (error) throw error;

      await fetchApplications();
      setSelectedApplication(null);
      setAdminNotes('');
      
      alert(`Başvuru ${newStatus === 'approved' ? 'onaylandı' : 'reddedildi'}!`);
    } catch (error) {
      console.error('Durum güncellenirken hata:', error);
      alert('Hata oluştu!');
    } finally {
      setActionLoading(false);
    }
  };

  const deleteApplication = async (applicationId: number) => {
    if (!window.confirm('Bu başvuruyu silmek istediğinizden emin misiniz?')) return;
    
    try {
      const { error } = await supabase
        .from('cashout_applications')
        .delete()
        .eq('id', applicationId);

      if (error) throw error;
      await fetchApplications();
      alert('Başvuru silindi!');
    } catch (error) {
      console.error('Silme hatası:', error);
      alert('Silme işlemi başarısız!');
    }
  };

  const filteredApplications = applications.filter(app => 
    filter === 'all' || app.status === filter
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#ff9800';
      case 'approved': return '#4caf50';
      case 'rejected': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Bekliyor';
      case 'approved': return 'Onaylandı';
      case 'rejected': return 'Reddedildi';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '300px',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            fontSize: '24px', 
            marginBottom: '10px' 
          }}>⏳</div>
          <p style={{ color: '#666' }}>Başvurular yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '20px', 
      backgroundColor: '#f8f9fa', 
      minHeight: '100vh' 
    }}>
      {/* Header */}
      <div style={{ 
        backgroundColor: 'white', 
        padding: '30px', 
        borderRadius: '12px', 
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          <div>
            <h1 style={{ 
              fontSize: '28px', 
              fontWeight: 'bold', 
              color: '#333', 
              margin: '0 0 10px 0',
              display: 'flex',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '32px', marginRight: '10px' }}>🪙</span>
              Puan Başvuruları
            </h1>
            <p style={{ 
              color: '#666', 
              margin: 0 
            }}>
              {filter === 'user-points' 
                ? 'Kullanıcıların anlık puan durumlarını görüntüleyin'
                : filter === 'verifications'
                ? 'Profil doğrulama başvurularını yönetin'
                : 'Kullanıcıların puan bozdurma başvurularını yönetin'
              }
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              color: '#2196f3' 
            }}>
              {filter === 'user-points' ? userPoints.length : filter === 'verifications' ? verifications.length : applications.length}
            </div>
            <div style={{ 
              fontSize: '14px', 
              color: '#999' 
            }}>
              {filter === 'user-points' ? 'Toplam Kullanıcı' : filter === 'verifications' ? 'Doğrulama Başvurusu' : 'Toplam Başvuru'}
            </div>
            {(filter === 'user-points' || filter === 'verifications') && (
              <button
                onClick={filter === 'verifications' ? fetchVerifications : fetchUserPoints}
                disabled={filter === 'verifications' ? verificationsLoading : pointsLoading}
                style={{
                  marginTop: '8px',
                  padding: '6px 12px',
                  backgroundColor: (filter === 'verifications' ? verificationsLoading : pointsLoading) ? '#f5f5f5' : '#2196f3',
                  color: (filter === 'verifications' ? verificationsLoading : pointsLoading) ? '#999' : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  cursor: (filter === 'verifications' ? verificationsLoading : pointsLoading) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {(filter === 'verifications' ? verificationsLoading : pointsLoading) ? '⏳ Yenileniyor...' : '🔄 Yenile'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '20px', 
        marginBottom: '20px' 
      }}>
        <div style={{ 
          backgroundColor: 'white', 
          padding: '20px', 
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ 
              width: '50px', 
              height: '50px', 
              backgroundColor: '#fff3cd', 
              borderRadius: '12px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              marginRight: '15px'
            }}>
              <span style={{ fontSize: '24px' }}>⏳</span>
            </div>
            <div>
              <p style={{ 
                fontSize: '24px', 
                fontWeight: 'bold', 
                margin: '0 0 5px 0',
                color: '#333'
              }}>
                {applications.filter(app => app.status === 'pending').length}
              </p>
              <p style={{ 
                fontSize: '14px', 
                color: '#666',
                margin: 0
              }}>
                Bekleyen
              </p>
            </div>
          </div>
        </div>
        
        <div style={{ 
          backgroundColor: 'white', 
          padding: '20px', 
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ 
              width: '50px', 
              height: '50px', 
              backgroundColor: '#d4edda', 
              borderRadius: '12px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              marginRight: '15px'
            }}>
              <span style={{ fontSize: '24px' }}>✅</span>
            </div>
            <div>
              <p style={{ 
                fontSize: '24px', 
                fontWeight: 'bold', 
                margin: '0 0 5px 0',
                color: '#333'
              }}>
                {applications.filter(app => app.status === 'approved').length}
              </p>
              <p style={{ 
                fontSize: '14px', 
                color: '#666',
                margin: 0
              }}>
                Onaylanan
              </p>
            </div>
          </div>
        </div>
        
        <div style={{ 
          backgroundColor: 'white', 
          padding: '20px', 
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ 
              width: '50px', 
              height: '50px', 
              backgroundColor: '#f8d7da', 
              borderRadius: '12px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              marginRight: '15px'
            }}>
              <span style={{ fontSize: '24px' }}>❌</span>
            </div>
            <div>
              <p style={{ 
                fontSize: '24px', 
                fontWeight: 'bold', 
                margin: '0 0 5px 0',
                color: '#333'
              }}>
                {applications.filter(app => app.status === 'rejected').length}
              </p>
              <p style={{ 
                fontSize: '14px', 
                color: '#666',
                margin: 0
              }}>
                Reddedilen
              </p>
            </div>
          </div>
        </div>
        
        <div style={{ 
          backgroundColor: 'white', 
          padding: '20px', 
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ 
              width: '50px', 
              height: '50px', 
              backgroundColor: '#cce5ff', 
              borderRadius: '12px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              marginRight: '15px'
            }}>
              <span style={{ fontSize: '24px' }}>₺</span>
            </div>
            <div>
              <p style={{ 
                fontSize: '24px', 
                fontWeight: 'bold', 
                margin: '0 0 5px 0',
                color: '#333'
              }}>
                ₺{applications.reduce((sum, app) => sum + app.amount_usd, 0).toFixed(0)}
              </p>
              <p style={{ 
                fontSize: '14px', 
                color: '#666',
                margin: 0
              }}>
                Toplam Tutar
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '12px', 
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <div style={{ 
          borderBottom: '1px solid #eee',
          display: 'flex',
          padding: '0 20px'
        }}>
          {[
            { key: 'all', label: 'Tümü', count: applications.length },
            { key: 'pending', label: 'Bekleyen', count: applications.filter(app => app.status === 'pending').length },
            { key: 'approved', label: 'Onaylanan', count: applications.filter(app => app.status === 'approved').length },
            { key: 'rejected', label: 'Reddedilen', count: applications.filter(app => app.status === 'rejected').length },
            { key: 'user-points', label: 'Kullanıcı Puanları', count: userPoints.length },
            { key: 'verifications', label: '🆔 Profil Doğrulama', count: verifications.filter(v => v.status === 'pending').length },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key as any)}
              style={{
                padding: '15px 20px',
                border: 'none',
                background: 'none',
                borderBottom: filter === tab.key ? '3px solid #2196f3' : '3px solid transparent',
                color: filter === tab.key ? '#2196f3' : '#666',
                fontWeight: filter === tab.key ? 'bold' : 'normal',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Applications Table or User Points Table */}
        <div style={{ overflowX: 'auto' }}>
          {filter === 'user-points' ? (
            // User Points Table
            pointsLoading ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '60px 20px',
                color: '#666'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
                <p>Kullanıcı puanları yükleniyor...</p>
              </div>
            ) : userPoints.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '60px 20px',
                color: '#999'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>👥</div>
                <h3 style={{ 
                  fontSize: '18px', 
                  fontWeight: 'bold', 
                  margin: '0 0 10px 0',
                  color: '#333'
                }}>
                  Kullanıcı bulunamadı
                </h3>
                <p style={{ margin: 0 }}>
                  Henüz puan alan kullanıcı bulunmuyor.
                </p>
              </div>
            ) : (
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ 
                      padding: '15px', 
                      textAlign: 'left', 
                      fontSize: '12px', 
                      fontWeight: 'bold', 
                      color: '#666',
                      textTransform: 'uppercase',
                      borderBottom: '1px solid #eee'
                    }}>
                      Kullanıcı
                    </th>
                    <th style={{ 
                      padding: '15px', 
                      textAlign: 'left', 
                      fontSize: '12px', 
                      fontWeight: 'bold', 
                      color: '#666',
                      textTransform: 'uppercase',
                      borderBottom: '1px solid #eee'
                    }}>
                      Mevcut Puan
                    </th>
                    <th style={{ 
                      padding: '15px', 
                      textAlign: 'left', 
                      fontSize: '12px', 
                      fontWeight: 'bold', 
                      color: '#666',
                      textTransform: 'uppercase',
                      borderBottom: '1px solid #eee'
                    }}>
                      Türk Lirası Değeri
                    </th>
                    <th style={{ 
                      padding: '15px', 
                      textAlign: 'left', 
                      fontSize: '12px', 
                      fontWeight: 'bold', 
                      color: '#666',
                      textTransform: 'uppercase',
                      borderBottom: '1px solid #eee'
                    }}>
                      E-posta
                    </th>
                    <th style={{ 
                      padding: '15px', 
                      textAlign: 'left', 
                      fontSize: '12px', 
                      fontWeight: 'bold', 
                      color: '#666',
                      textTransform: 'uppercase',
                      borderBottom: '1px solid #eee'
                    }}>
                      Kayıt Tarihi
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {userPoints.map((user, index) => (
                    <tr 
                      key={user.id}
                      style={{ 
                        borderBottom: '1px solid #f0f0f0',
                        backgroundColor: index % 2 === 0 ? '#fafafa' : 'white'
                      }}
                    >
                      <td style={{ padding: '15px' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <div style={{ 
                            width: '40px', 
                            height: '40px', 
                            borderRadius: '50%', 
                            backgroundColor: user.avatar_url ? 'transparent' : '#f0f0f0',
                            marginRight: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundImage: user.avatar_url ? `url(${user.avatar_url})` : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                          }}>
                            {!user.avatar_url && (
                              <span style={{ 
                                fontSize: '18px',
                                color: '#666'
                              }}>
                                {user.first_name?.charAt(0)?.toUpperCase() || 'U'}
                              </span>
                            )}
                          </div>
                          <div>
                            <p style={{ 
                              margin: '0 0 4px 0', 
                              fontWeight: 'bold',
                              fontSize: '14px',
                              color: '#333'
                            }}>
                              {user.first_name} {user.last_name}
                            </p>
                            <p style={{ 
                              margin: 0, 
                              fontSize: '12px',
                              color: '#666'
                            }}>
                              ID: {user.id.substring(0, 8)}...
                            </p>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '15px' }}>
                        <div style={{ 
                          display: 'inline-flex',
                          alignItems: 'center',
                          backgroundColor: user.points > 0 ? '#e8f5e8' : '#f5f5f5',
                          color: user.points > 0 ? '#2e7d32' : '#666',
                          padding: '6px 12px',
                          borderRadius: '20px',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}>
                          🪙 {user.points || 0}
                        </div>
                      </td>
                      <td style={{ padding: '15px' }}>
                        <div style={{ 
                          color: '#2196f3',
                          fontWeight: 'bold',
                          fontSize: '14px'
                        }}>
                          ₺{((user.points || 0) * 0.20).toFixed(2)}
                        </div>
                      </td>
                      <td style={{ padding: '15px' }}>
                        <div style={{ 
                          fontSize: '14px',
                          color: '#333'
                        }}>
                          {user.email}
                        </div>
                      </td>
                      <td style={{ padding: '15px' }}>
                        <div style={{ 
                          fontSize: '12px',
                          color: '#666'
                        }}>
                          {new Date(user.created_at).toLocaleDateString('tr-TR', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : filter === 'verifications' ? (
            // 🆕 Profil Doğrulama Tablosu
            verificationsLoading ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '60px 20px',
                color: '#666'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
                <p>Doğrulama başvuruları yükleniyor...</p>
              </div>
            ) : verifications.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '60px 20px',
                color: '#999'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>🆔</div>
                <h3 style={{ 
                  fontSize: '18px', 
                  fontWeight: 'bold', 
                  margin: '0 0 10px 0',
                  color: '#333'
                }}>
                  Doğrulama başvurusu bulunamadı
                </h3>
                <p style={{ margin: 0 }}>
                  Henüz profil doğrulama başvurusu yapılmamış.
                </p>
              </div>
            ) : (
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '15px', textAlign: 'left', fontSize: '12px', fontWeight: 'bold', color: '#666', textTransform: 'uppercase', borderBottom: '1px solid #eee' }}>
                      Kullanıcı
                    </th>
                    <th style={{ padding: '15px', textAlign: 'left', fontSize: '12px', fontWeight: 'bold', color: '#666', textTransform: 'uppercase', borderBottom: '1px solid #eee' }}>
                      Ad Soyad
                    </th>
                    <th style={{ padding: '15px', textAlign: 'left', fontSize: '12px', fontWeight: 'bold', color: '#666', textTransform: 'uppercase', borderBottom: '1px solid #eee' }}>
                      Telefon
                    </th>
                    <th style={{ padding: '15px', textAlign: 'left', fontSize: '12px', fontWeight: 'bold', color: '#666', textTransform: 'uppercase', borderBottom: '1px solid #eee' }}>
                      E-posta
                    </th>
                    <th style={{ padding: '15px', textAlign: 'left', fontSize: '12px', fontWeight: 'bold', color: '#666', textTransform: 'uppercase', borderBottom: '1px solid #eee' }}>
                      Tarih
                    </th>
                    <th style={{ padding: '15px', textAlign: 'left', fontSize: '12px', fontWeight: 'bold', color: '#666', textTransform: 'uppercase', borderBottom: '1px solid #eee' }}>
                      Durum
                    </th>
                    <th style={{ padding: '15px', textAlign: 'left', fontSize: '12px', fontWeight: 'bold', color: '#666', textTransform: 'uppercase', borderBottom: '1px solid #eee' }}>
                      İşlemler
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {verifications.map((verification) => (
                    <tr key={verification.id} style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: verification.status === 'pending' ? '#fffdf0' : 'white' }}>
                      <td style={{ padding: '15px' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {verification.user?.profile_pic_url ? (
                            <img
                              src={verification.user.profile_pic_url}
                              alt={verification.user?.name || 'Kullanıcı'}
                              style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', marginRight: '12px', border: '2px solid #F78D93' }}
                            />
                          ) : (
                            <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#F78D93', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '12px', fontSize: '16px', fontWeight: 'bold', color: 'white' }}>
                              {verification.name ? verification.name.charAt(0).toUpperCase() : 'U'}
                            </div>
                          )}
                          <div>
                            <div style={{ fontWeight: '500', color: '#333' }}>{verification.user?.name || 'Bilinmiyor'}</div>
                            <div style={{ fontSize: '12px', color: '#999' }}>ID: {verification.user_id.slice(0, 8)}...</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '15px' }}>
                        <div style={{ fontWeight: '500', color: '#333' }}>{verification.name} {verification.surname}</div>
                      </td>
                      <td style={{ padding: '15px' }}>
                        <div style={{ color: '#333' }}>{verification.phone}</div>
                      </td>
                      <td style={{ padding: '15px' }}>
                        <div style={{ color: '#333' }}>{verification.email}</div>
                      </td>
                      <td style={{ padding: '15px' }}>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {new Date(verification.created_at).toLocaleDateString('tr-TR', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </td>
                      <td style={{ padding: '15px' }}>
                        <span style={{
                          padding: '6px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          backgroundColor: verification.status === 'pending' ? '#fff3cd' : verification.status === 'approved' ? '#d4edda' : '#f8d7da',
                          color: verification.status === 'pending' ? '#856404' : verification.status === 'approved' ? '#155724' : '#721c24'
                        }}>
                          {verification.status === 'pending' ? '⏳ Bekliyor' : verification.status === 'approved' ? '✅ Onaylı' : '❌ Reddedildi'}
                        </span>
                      </td>
                      <td style={{ padding: '15px' }}>
                        {verification.status === 'pending' && (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => updateVerificationStatus(verification.id, 'approved', '')}
                              disabled={actionLoading}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: '#4caf50',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: 'bold'
                              }}
                            >
                              ✅ Onayla
                            </button>
                            <button
                              onClick={() => updateVerificationStatus(verification.id, 'rejected', '')}
                              disabled={actionLoading}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: 'bold'
                              }}
                            >
                              ❌ Reddet
                            </button>
                          </div>
                        )}
                        {verification.status !== 'pending' && verification.admin_notes && (
                          <div style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                            {verification.admin_notes}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : filteredApplications.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '60px 20px',
              color: '#999'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>📋</div>
              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: 'bold', 
                margin: '0 0 10px 0',
                color: '#333'
              }}>
                Başvuru bulunamadı
              </h3>
              <p style={{ margin: 0 }}>
                {filter === 'all' 
                  ? 'Henüz hiç puan başvurusu yapılmamış.'
                  : `${filter} durumunda başvuru bulunmuyor.`
                }
              </p>
            </div>
          ) : (
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ 
                    padding: '15px', 
                    textAlign: 'left', 
                    fontSize: '12px', 
                    fontWeight: 'bold', 
                    color: '#666',
                    textTransform: 'uppercase',
                    borderBottom: '1px solid #eee'
                  }}>
                    Kullanıcı
                  </th>
                  <th style={{ 
                    padding: '15px', 
                    textAlign: 'left', 
                    fontSize: '12px', 
                    fontWeight: 'bold', 
                    color: '#666',
                    textTransform: 'uppercase',
                    borderBottom: '1px solid #eee'
                  }}>
                    Puan & Tutar
                  </th>
                  <th style={{ 
                    padding: '15px', 
                    textAlign: 'left', 
                    fontSize: '12px', 
                    fontWeight: 'bold', 
                    color: '#666',
                    textTransform: 'uppercase',
                    borderBottom: '1px solid #eee'
                  }}>
                    İletişim
                  </th>
                  <th style={{ 
                    padding: '15px', 
                    textAlign: 'left', 
                    fontSize: '12px', 
                    fontWeight: 'bold', 
                    color: '#666',
                    textTransform: 'uppercase',
                    borderBottom: '1px solid #eee'
                  }}>
                    Durum
                  </th>
                  <th style={{ 
                    padding: '15px', 
                    textAlign: 'left', 
                    fontSize: '12px', 
                    fontWeight: 'bold', 
                    color: '#666',
                    textTransform: 'uppercase',
                    borderBottom: '1px solid #eee'
                  }}>
                    Tarih
                  </th>
                  <th style={{ 
                    padding: '15px', 
                    textAlign: 'left', 
                    fontSize: '12px', 
                    fontWeight: 'bold', 
                    color: '#666',
                    textTransform: 'uppercase',
                    borderBottom: '1px solid #eee'
                  }}>
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredApplications.map((app, index) => (
                  <tr 
                    key={app.id} 
                    style={{ 
                      borderBottom: '1px solid #eee',
                      backgroundColor: index % 2 === 0 ? 'white' : '#fafafa'
                    }}
                  >
                    <td style={{ padding: '15px' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ 
                          width: '40px', 
                          height: '40px', 
                          backgroundColor: '#e3f2fd', 
                          borderRadius: '50%', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          marginRight: '12px'
                        }}>
                          <span style={{ 
                            color: '#1976d2', 
                            fontWeight: 'bold', 
                            fontSize: '14px' 
                          }}>
                            {app.user_id.substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div style={{ 
                            fontSize: '14px', 
                            fontWeight: 'bold', 
                            color: '#333' 
                          }}>
                            #{app.id}
                          </div>
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#999',
                            fontFamily: 'monospace'
                          }}>
                            {app.user_id.substring(0, 12)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '15px' }}>
                      <div>
                        <div style={{ 
                          fontSize: '14px', 
                          fontWeight: 'bold', 
                          color: '#1976d2',
                          marginBottom: '4px'
                        }}>
                          {app.points_requested.toLocaleString()} puan
                        </div>
                        <div style={{ 
                          fontSize: '16px', 
                          fontWeight: 'bold', 
                          color: '#4caf50'
                        }}>
                          ₺{app.amount_usd.toFixed(0)}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '15px' }}>
                      <div>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          marginBottom: '4px'
                        }}>
                          <span style={{ marginRight: '6px' }}>📧</span>
                          <span style={{ 
                            fontSize: '13px',
                            maxWidth: '150px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {app.email}
                          </span>
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center'
                        }}>
                          <span style={{ marginRight: '6px' }}>📱</span>
                          <span style={{ fontSize: '13px' }}>{app.phone}</span>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '15px' }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: 'white',
                        backgroundColor: getStatusColor(app.status)
                      }}>
                        {getStatusText(app.status)}
                      </span>
                    </td>
                    <td style={{ padding: '15px' }}>
                      <div>
                        <div style={{ 
                          fontSize: '13px', 
                          color: '#333',
                          marginBottom: '2px'
                        }}>
                          {new Date(app.created_at).toLocaleDateString('tr-TR', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </div>
                        <div style={{ 
                          fontSize: '11px', 
                          color: '#999'
                        }}>
                          {new Date(app.created_at).toLocaleTimeString('tr-TR', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '15px' }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px' 
                      }}>
                        {app.status === 'pending' && (
                          <>
                            <button
                              onClick={() => updateApplicationStatus(app.id, 'approved', '')}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#d4edda',
                                color: '#155724',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                              }}
                              title="Onayla"
                            >
                              ✅ Onayla
                            </button>
                            <button
                              onClick={() => updateApplicationStatus(app.id, 'rejected', '')}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#f8d7da',
                                color: '#721c24',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                              }}
                              title="Reddet"
                            >
                              ❌ Reddet
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => {
                            setSelectedApplication(app);
                            setAdminNotes(app.admin_notes || '');
                          }}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#e2e6ea',
                            color: '#383d41',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            cursor: 'pointer'
                          }}
                          title="Detay"
                        >
                          📋 Detay
                        </button>
                        <button
                          onClick={() => deleteApplication(app.id)}
                          style={{
                            padding: '6px 8px',
                            backgroundColor: '#f5c6cb',
                            color: '#721c24',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                          title="Sil"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedApplication && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
          }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #eee',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#333',
                display: 'flex',
                alignItems: 'center'
              }}>
                <span style={{ fontSize: '24px', marginRight: '8px' }}>📋</span>
                Başvuru Detayı #{selectedApplication.id}
              </h2>
              <button
                onClick={() => setSelectedApplication(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: '#999',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              {/* Application Info */}
              <div style={{
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#333',
                  marginBottom: '15px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <span style={{ fontSize: '18px', marginRight: '8px' }}>ℹ️</span>
                  Başvuru Bilgileri
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '15px'
                }}>
                  <div>
                    <label style={{
                      fontSize: '12px',
                      fontWeight: 'bold',
                      color: '#666',
                      textTransform: 'uppercase'
                    }}>
                      Kullanıcı ID
                    </label>
                    <p style={{
                      fontSize: '13px',
                      fontFamily: 'monospace',
                      backgroundColor: 'white',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      margin: '5px 0 0 0'
                    }}>
                      {selectedApplication.user_id}
                    </p>
                  </div>
                  <div>
                    <label style={{
                      fontSize: '12px',
                      fontWeight: 'bold',
                      color: '#666',
                      textTransform: 'uppercase'
                    }}>
                      Puan
                    </label>
                    <p style={{
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: '#1976d2',
                      margin: '5px 0 0 0'
                    }}>
                      {selectedApplication.points_requested.toLocaleString()} puan
                    </p>
                  </div>
                  <div>
                    <label style={{
                      fontSize: '12px',
                      fontWeight: 'bold',
                      color: '#666',
                      textTransform: 'uppercase'
                    }}>
                      Tutar
                    </label>
                    <p style={{
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: '#4caf50',
                      margin: '5px 0 0 0'
                    }}>
                      ₺{selectedApplication.amount_usd.toFixed(0)}
                    </p>
                  </div>
                  <div>
                    <label style={{
                      fontSize: '12px',
                      fontWeight: 'bold',
                      color: '#666',
                      textTransform: 'uppercase'
                    }}>
                      Durum
                    </label>
                    <div style={{ marginTop: '5px' }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: 'white',
                        backgroundColor: getStatusColor(selectedApplication.status)
                      }}>
                        {getStatusText(selectedApplication.status)}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label style={{
                      fontSize: '12px',
                      fontWeight: 'bold',
                      color: '#666',
                      textTransform: 'uppercase'
                    }}>
                      E-posta
                    </label>
                    <p style={{
                      fontSize: '13px',
                      backgroundColor: 'white',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      margin: '5px 0 0 0',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <span style={{ marginRight: '8px' }}>📧</span>
                      {selectedApplication.email}
                    </p>
                  </div>
                  <div>
                    <label style={{
                      fontSize: '12px',
                      fontWeight: 'bold',
                      color: '#666',
                      textTransform: 'uppercase'
                    }}>
                      Telefon
                    </label>
                    <p style={{
                      fontSize: '13px',
                      backgroundColor: 'white',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      margin: '5px 0 0 0',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <span style={{ marginRight: '8px' }}>📱</span>
                      {selectedApplication.phone}
                    </p>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{
                      fontSize: '12px',
                      fontWeight: 'bold',
                      color: '#666',
                      textTransform: 'uppercase'
                    }}>
                      Başvuru Tarihi
                    </label>
                    <p style={{
                      fontSize: '13px',
                      backgroundColor: 'white',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      margin: '5px 0 0 0'
                    }}>
                      {new Date(selectedApplication.created_at).toLocaleString('tr-TR')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Admin Notes */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#333',
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <span style={{ fontSize: '18px', marginRight: '8px' }}>📝</span>
                  Admin Notu
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Admin notu ekleyin..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px',
                    resize: 'vertical',
                    minHeight: '80px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '10px',
                paddingTop: '15px',
                borderTop: '1px solid #eee'
              }}>
                {selectedApplication.status === 'pending' && (
                  <>
                    <button
                      onClick={() => updateApplicationStatus(selectedApplication.id, 'approved', adminNotes)}
                      disabled={actionLoading}
                      style={{
                        flex: 1,
                        backgroundColor: '#4caf50',
                        color: 'white',
                        padding: '12px 20px',
                        borderRadius: '8px',
                        border: 'none',
                        fontWeight: 'bold',
                        cursor: actionLoading ? 'not-allowed' : 'pointer',
                        opacity: actionLoading ? 0.7 : 1
                      }}
                    >
                      {actionLoading ? '⏳ İşleniyor...' : '✅ Onayla'}
                    </button>
                    <button
                      onClick={() => updateApplicationStatus(selectedApplication.id, 'rejected', adminNotes)}
                      disabled={actionLoading}
                      style={{
                        flex: 1,
                        backgroundColor: '#f44336',
                        color: 'white',
                        padding: '12px 20px',
                        borderRadius: '8px',
                        border: 'none',
                        fontWeight: 'bold',
                        cursor: actionLoading ? 'not-allowed' : 'pointer',
                        opacity: actionLoading ? 0.7 : 1
                      }}
                    >
                      {actionLoading ? '⏳ İşleniyor...' : '❌ Reddet'}
                    </button>
                  </>
                )}
                <button
                  onClick={() => {
                    setSelectedApplication(null);
                    setAdminNotes('');
                  }}
                  style={{
                    padding: '12px 20px',
                    backgroundColor: '#9e9e9e',
                    color: 'white',
                    borderRadius: '8px',
                    border: 'none',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PointApplicationsPage; 